import { User } from '@/contexts/UserContext';

/**
 * Filter shipments by origin OR destination warehouse
 * Admin users see everything, warehouse users see shipments coming FROM or TO their warehouse
 */
export function filterShipmentsByWarehouse<T extends { destinationWarehouse?: string; originWarehouse?: string }>(
  items: T[],
  user: User | null
): T[] {
  if (!user) return [];

  // Admin sees everything
  if (user.role === 'admin') {
    return items;
  }

  // Warehouse users see shipments where originWarehouse OR destinationWarehouse matches their warehouse
  // Use case-insensitive comparison and trim whitespace to handle data inconsistencies
  if (user.role === 'warehouse' && user.warehouseCode) {
    const userCode = user.warehouseCode.trim().toUpperCase();

    return items.filter(item => {
      const destCode = (item.destinationWarehouse || '').trim().toUpperCase();
      const originCode = (item.originWarehouse || '').trim().toUpperCase();
      return destCode === userCode || originCode === userCode;
    });
  }

  return [];
}

/**
 * Filter trips and other items based on user's warehouse access
 * Admin users see everything, warehouse users see items with matching origin OR destination
 */
export function filterByWarehouse<T extends { destinationWarehouse?: string; destination?: string; originWarehouse?: string; origin?: string }>(
  items: T[],
  user: User | null
): T[] {
  if (!user) return [];

  // Admin sees everything
  if (user.role === 'admin') {
    return items;
  }

  // Warehouse users only see items for their warehouse
  if (user.role === 'warehouse') {
    // Get all possible warehouse identifiers for the user
    const userWarehouses = [
      user.warehouseCode,
      user.warehouseName,
      user.warehouse, // Direct warehouse property if it exists
    ].filter(Boolean); // Remove any undefined/null values

    return items.filter(item => {
      // Get all possible warehouse identifiers for the item
      const itemWarehouses = [
        item.destinationWarehouse,
        item.destination,
        item.originWarehouse,
        item.origin,
      ].filter(Boolean);

      // Check if any of the item's warehouses match any of the user's warehouses
      const shouldShow = itemWarehouses.some(itemWarehouse =>
        userWarehouses.some(userWarehouse =>
          itemWarehouse === userWarehouse
        )
      );

      return shouldShow;
    });
  }

  return [];
}

/**
 * Special filter for invoices that checks shipment destinations
 */
export function filterInvoicesByWarehouse<T extends { destinationWarehouse?: string; shipments?: any[] }>(
  items: T[],
  user: User | null
): T[] {
  if (!user) return [];

  // Admin sees everything
  if (user.role === 'admin') {
    return items;
  }

  // Warehouse users only see invoices for their warehouse
  if (user.role === 'warehouse' && (user.warehouseCode || user.warehouseName)) {
    return items.filter(item => {
      // If invoice has direct warehouse assignment, use it
      if (item.destinationWarehouse) {
        // Check against both code and name for maximum compatibility
        return item.destinationWarehouse === user.warehouseCode ||
               item.destinationWarehouse === user.warehouseName;
      }

      // If no direct warehouse assignment, don't show to warehouse users
      // This ensures invoices without warehouse assignment are only visible to admins
      return false;
    });
  }

  return [];
}

/**
 * Check if a user can create/edit items for a specific warehouse
 */
export function canModifyForWarehouse(
  warehouseCode: string,
  user: User | null
): boolean {
  if (!user) return false;

  // Admin can modify everything
  if (user.role === 'admin') return true;

  // Warehouse users can only modify their own warehouse
  if (user.role === 'warehouse' && user.warehouseCode) {
    return warehouseCode === user.warehouseCode;
  }

  return false;
}

/**
 * Get available warehouses for a user
 */
export function getAvailableWarehouses(
  allWarehouses: any[],
  user: User | null
): any[] {
  if (!user) return [];

  // Admin sees all warehouses
  if (user.role === 'admin') {
    return allWarehouses;
  }

  // Warehouse users only see their warehouse
  if (user.role === 'warehouse' && user.warehouseCode) {
    return allWarehouses.filter(w => w.code === user.warehouseCode);
  }

  return [];
}